/**
 * MessageTemplateContainer
 * --
 *
 */

import React, { useState } from 'react';
import MessageTemplatePresenter from './MessageTemplatePresenter';
import MessageAlert from 'utils';
import {
  useGetPushTemplates,
  useUpdatePushTemplate,
  pushTemplateQueryKeys,
} from 'hooks/PushTemplateHooks';
import {
  useGetEmailTemplates,
  useUpdateEmailTemplate,
  emailTemplateQueryKeys,
} from 'hooks/EmailTemplateHooks';
import {
  useGetSMSTemplates,
  useUpdateSMSTemplate,
  SMSTemplateQueryKeys,
} from 'hooks/SMSTemplateHooks';
import { useQueryClient } from '@tanstack/react-query';

/**----------------------------------------
 * [Component] NotificationListContainer
 * ----------------------------------------
 */
const MessageTemplateContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== Data ===== */
  const query = {};
  const options = {
    select: (res) => {
      const { status, message, data } = res;

      if (status !== 200) {
        console.warn(message);
        return [];
      }

      return data;
    },
    initialData: [],
  };

  const queryClient = useQueryClient();

  const { data: EmailTemplates } = useGetEmailTemplates(query, options);
  const { data: SMSTemplates } = useGetSMSTemplates(query, options);
  const { data: PushTemplates } = useGetPushTemplates(query, options);

  /* ===== Mutate ===== */
  const { updatePushTemplate } = useUpdatePushTemplate();
  const { updateEmailTemplate } = useUpdateEmailTemplate();
  const { updateSMSTemplate } = useUpdateSMSTemplate();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 탭키
  const [tapKey, setTapKey] = useState('');
  // 선택 템플릿
  const [selectedData, setSelectedData] = useState(null);
  // 변경값
  const [tempContent, setTempContent] = useState(null);
  // 상위 탭
  const [type, setType] = React.useState('PUSH');

  /* ===== VARIABLE ===== */
  const getQueryKey = {
    PUSH: (params) => pushTemplateQueryKeys.getPushTemplates(params),
    SMS: (params) => SMSTemplateQueryKeys.getSMSTemplates(params),
    EMAIL: (params) => emailTemplateQueryKeys.getEmailTemplates(params),
  };

  /* ===== FUNCTION ===== */
  /**
   * 템플릿 선택
   * --
   * @param {string} key
   * @param {object} value
   * @returns
   */
  const handleTabChange = (key) => {
    const findData = Data[type]?.find(
      (_item) => _item[`${type.toLocaleLowerCase()}_template_id`] === key
    );

    setTapKey(key);
    setTempContent(findData?.[`${type.toLocaleLowerCase()}_content`]);
    setSelectedData({
      ...findData,
      key: key,
    });
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    let newData = { ...tempContent };

    newData[t] = v;
    const d = {
      ...newData,
    };
    setTempContent(d);

    if (callback) callback(v);
  };

  /**
   * 데이터 수정
   * --
   * @param {string} t
   * @returns
   */
  const handleSubmit = async (t = 'create', value = null) => {
    let submitType = t.toLocaleUpperCase();

    try {
      const isExist = !!selectedData;

      const confirm = (message) => {
        return window.confirm(message);
      };

      if (isExist && submitType === 'CREATE' && selectedData.push_template_id)
        submitType = 'UPDATE';

      let queryKey;
      switch (submitType) {
        case 'RESET':
          // 선택된 값이 없을 경우 리턴
          if (!isExist) return;

          setTempContent(selectedData?.[`${type.toLocaleLowerCase()}_content`]);
          return;
        case 'UPDATE':
          if (!confirm('템플릿을 수정하시겠습니까?')) return;

          const update =
            type === 'SMS'
              ? {
                  [`${type.toLocaleLowerCase()}_template_id`]:
                    selectedData?.[`${type.toLocaleLowerCase()}_template_id`],
                  [`${type.toLocaleLowerCase()}_content`]: tempContent,
                }
              : {
                  [`${type.toLocaleLowerCase()}_template_id`]:
                    selectedData?.[`${type.toLocaleLowerCase()}_template_id`],
                  [`${type.toLocaleLowerCase()}_content`]: {
                    ...selectedData.push_content,
                    ...tempContent,
                  },
                };

          const mutate = {
            PUSH: updatePushTemplate,
            EMAIL: updateEmailTemplate,
            SMS: updateSMSTemplate,
          };

          const { status: updateStatus } = await mutate[type](update);

          if (updateStatus !== 200) {
            MessageAlert.warning('템플릿 수정에 실패하였습니다!');
            return;
          }

          MessageAlert.success('템플릿이 성공적으로 저장되었습니다!');

          queryKey = getQueryKey[type](query);
          queryClient.invalidateQueries({
            queryKey: queryKey.queryKey,
          });

          return;
        case 'ENABLE':
          if (
            !confirm(
              `템플릿을 ${
                value.is_enable === 'N' ? '비활성화' : '활성화'
              } 하시겠습니까?`
            )
          )
            return;

          const enableMutate = {
            PUSH: updatePushTemplate,
            EMAIL: updateEmailTemplate,
            SMS: updateSMSTemplate,
          };

          const { status: enableStatus } = await enableMutate[type](value);

          if (enableStatus !== 200) {
            MessageAlert.warning('템플릿 수정에 실패하였습니다!');
            return;
          }

          MessageAlert.success('템플릿이 성공적으로 저장되었습니다!');
          queryKey = getQueryKey[type](query);
          queryClient.invalidateQueries({
            queryKey: queryKey.queryKey,
          });

          setSelectedData((prev) => ({
            ...prev,
            is_enable: value.is_enable,
          }));

          return;
        default:
          MessageAlert.info('템플릿 저장에 실패하였습니다');
          return;
      }
    } catch (err) {
      MessageAlert.error('템플릿 저장에 실패하였습니다');
      console.error(`[handleSubmit] Error!! ${err}`);
      return;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 템플릿 데이터 정제
   * --
   */
  const Data = React.useMemo(() => {
    return {
      PUSH: PushTemplates,
      EMAIL: EmailTemplates,
      SMS: SMSTemplates,
    };
  }, [EmailTemplates, SMSTemplates, PushTemplates]);

  React.useEffect(() => {
    setTempContent(null);
  }, [type]);

  /* ===== RENDER ===== */
  return (
    <>
      <MessageTemplatePresenter
        data={Data}
        onTabChange={handleTabChange}
        tapKey={tapKey}
        selectedData={selectedData}
        tempContent={tempContent}
        onChange={handleChange}
        onSubmit={handleSubmit}
        type={type}
        setType={setType}
        setTempContent={setTempContent}
      />
    </>
  );
};

export default MessageTemplateContainer;
